import { AwardInterface } from '~/models/interfaces'
import { Background, Container } from './Roulette.styles'
import { Box, Button, Flex, Typography } from '~/ds-components'
import { colors, getSpacing, spacings } from '~/assets/styles'
import { CompleteRouletteDialogContent } from '~/components/CompleteRouletteDialogContent'
import { PrizeDrawTypeEnum } from '~/models/enums'
import { PrizeHubService } from '~/services'
import { PrizeStocksInterface, PrizeStocksRafflePrizeInterface } from '~/models/interfaces/PrizeHub'
import { svgPaths } from '~/assets/images'
import { useDialog } from '~/utils'
import { useParams } from 'react-router'
import { useState, useEffect, useCallback } from 'react'
import { Wheel } from 'react-custom-roulette'
import { WheelBorder } from '~/components/WheelBorder'
import signature from '~/assets/images/signature@2x.webp'
import { BUCKET_URL } from '~/config/bucket'

const Roulette = () => {
  const { show } = useDialog()
  const { contractNumber, campaignSlug } = useParams()

  const [isDisable, setIsDisable] = useState(false)
  const [prizeNumber, setPrizeNumber] = useState(0)
  const [prizeStocksState, setPrizeStocksState] = useState<PrizeStocksInterface>()
  const [mustStartSpinning, setMustStartSpinning] = useState(false)
  const [dataState, setDataState] = useState<{
    isLoading: boolean
    data: AwardInterface[]
  }>({ isLoading: true, data: [] })

  const isThematicWheel = prizeStocksState?.prizeDrawType === PrizeDrawTypeEnum.THEMATIC_WHEEL

  const getMaxRafflePrize = useCallback(() => {
    const maxPrize = dataState.data.reduce(
      (max, curr) =>
        Number(curr.option.replace(/[^0-9]/g, '')) > Number(max.option.replace(/[^0-9]/g, ''))
          ? curr
          : max,
      dataState.data[0],
    )

    return maxPrize.option
  }, [dataState.data])

  const handleOnClickSpin = useCallback(() => {
    if (dataState.data.length === 0) return

    const audio = new Audio(`${BUCKET_URL}/mecanismo-sorteio/assets/sound-effects/roda.mp3`)

    setTimeout(() => {
      audio.play().catch((error) => {
        console.log('Autoplay bloqueado:', error)
      })
    }, 1000)

    const prizeIndex = dataState.data.findIndex((elem) => elem.isAwarded)

    setIsDisable(true)
    setPrizeNumber(prizeIndex)
    setMustStartSpinning(true)
  }, [dataState.data])

  const setColorDraw = useCallback(
    (prizeNumber: number) => {
      if (prizeNumber === -1) return

      dataState.data[prizeNumber].style = {
        backgroundColor: '#33cc4c',
        textColor: colors.white,
      }

      setDataState(dataState)
    },
    [dataState],
  )

  const handleOnStopSpinning = useCallback(async () => {
    if (prizeNumber === -1) return

    const dialogId = 'complete-roulette-dialog-id'
    const audio = new Audio(
      `${BUCKET_URL}/mecanismo-sorteio/assets/sound-effects/premio-revelado.wav`,
    )

    audio.play().catch((error) => {
      console.log('Autoplay bloqueado:', error)
    })

    setIsDisable(true)
    setColorDraw(prizeNumber)
    setMustStartSpinning(false)

    setTimeout(
      () =>
        show({
          dialogId,
          size: 'sm',
          closableOverlay: false,
          header: {
            title: '',
            sx: { marginBottom: 0, border: 'none', padding: `${spacings[8]} ${spacings[8]} 0` },
          },
          content: (
            <CompleteRouletteDialogContent dataState={dataState} prizeNumber={prizeNumber} />
          ),
          slotProps: { body: { sx: { paddingTop: 0 } } },
        }),
      3_000,
    )

    if (campaignSlug && contractNumber)
      await PrizeHubService.sendPrizeViewed({ campaignSlug, contractNumber })
  }, [campaignSlug, contractNumber, dataState, prizeNumber, setColorDraw, show])

  const renderContent = () => {
    return (
      <Background
        backgroundColor={dataState.isLoading ? '#ffffff' : colors.brand['viacerta-blue']}
        backgroundDesktop={
          isThematicWheel ? prizeStocksState.campaignAssets.desktopBackgroundUrl : undefined
        }
        backgroundMobile={
          isThematicWheel ? prizeStocksState.campaignAssets.mobileBackgroundUrl : undefined
        }
        isThematicWheel={isThematicWheel}
      >
        <Container>
          {dataState.isLoading && dataState.data.length === 0 ? (
            <></>
          ) : (
            <>
              <Flex
                alignItems="center"
                className="header"
                flexDirection="column"
                paddingInline={spacings[20]}
              >
                <img
                  src={svgPaths.viacertaLogo}
                  alt="VIACERTA logo"
                  style={{ width: '60%', maxWidth: '180px' }}
                />

                <Flex display="grid" sx={{ placeItems: 'center' }}>
                  <img
                    src={
                      isThematicWheel
                        ? prizeStocksState.campaignAssets.campaignSignatureUrl
                        : signature
                    }
                    alt="Roda de Prêmios VIACERTA"
                    style={{
                      gridArea: '1 / 1',
                      marginTop: spacings[20],
                      maxWidth: isThematicWheel ? '414px' : '455px',
                      width: '100%',
                    }}
                  />

                  {!isThematicWheel && (
                    <img
                      alt="Falling Coins"
                      className="falling-coins"
                      src={svgPaths.coinsDrop}
                      style={{
                        gridArea: '1 / 1',
                        maxWidth: '64%',
                      }}
                    />
                  )}
                </Flex>
              </Flex>

              <Flex
                alignItems="center"
                className="biggest-prize"
                gap={6}
                flexWrap="wrap"
                justifyContent="center"
                sx={{ zIndex: 1 }}
              >
                <Typography
                  color="white"
                  textAlign="center"
                  textTransform="uppercase"
                  variant="3xLarge"
                >
                  Ganhe até
                </Typography>

                <Typography as="span" bold color="white" variant="5xLarge">
                  {getMaxRafflePrize()}
                </Typography>
              </Flex>

              {isThematicWheel ? (
                <Flex
                  width={{ xs: '127%', sm: '100%' }}
                  padding={getSpacing(32, 16)}
                  gridArea="roulette"
                  justifyContent="center"
                  sx={{ zIndex: 2 }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      width: '100%',
                      maxWidth: '512px',
                      aspectRatio: '1 / 1',
                    }}
                  >
                    <WheelBorder
                      slotProps={{
                        mainRing: { color: prizeStocksState?.campaignAssets.wheelColorHex },
                      }}
                    >
                      <Wheel
                        backgroundColors={[
                          colors.grey.white,
                          prizeStocksState?.campaignAssets.wheelColorHex,
                        ]}
                        data={dataState.data}
                        fontSize={20}
                        mustStartSpinning={mustStartSpinning}
                        onStopSpinning={handleOnStopSpinning}
                        outerBorderColor={colors.white}
                        outerBorderWidth={5}
                        spinDuration={0.9}
                        pointerProps={{ style: { display: 'none' } }}
                        prizeNumber={prizeNumber}
                        radiusLineWidth={0}
                        textColors={[
                          prizeStocksState?.campaignAssets.wheelColorHex,
                          colors.grey.white,
                        ]}
                      />

                      <img className="arrow" alt="Arrow" src={svgPaths.arrow} />

                      <Button
                        size="large"
                        version="v2"
                        disabled={isDisable}
                        onClick={handleOnClickSpin}
                      >
                        Girar
                      </Button>
                    </WheelBorder>
                  </Box>
                </Flex>
              ) : (
                <Flex
                  alignItems="center"
                  className="roulette"
                  flexDirection="column"
                  justifyContent="center"
                  sx={{ position: 'relative' }}
                >
                  <Box sx={{ maxWidth: '512px', position: 'relative', width: '126%' }}>
                    <img
                      src={svgPaths.rouletteBorder}
                      alt="Roulette"
                      style={{ width: '100%', maxWidth: '552px', zIndex: 0 }}
                    />

                    <Box
                      className="roulette-container"
                      sx={{
                        aspectRatio: '1 / 1',
                        position: 'absolute',
                        left: '9.60144928%',
                        top: '5.79710145%',
                        width: '80.79710145%',
                        zIndex: 1,
                      }}
                    >
                      <Wheel
                        backgroundColors={[colors.grey.white, colors.blue[500]]}
                        data={dataState.data}
                        fontSize={20}
                        mustStartSpinning={mustStartSpinning}
                        onStopSpinning={handleOnStopSpinning}
                        outerBorderColor={colors.gold[300]}
                        outerBorderWidth={1}
                        spinDuration={0.9}
                        pointerProps={{
                          style: {
                            top: '-16px',
                            right: '-16px',
                            width: '27%',
                          },
                          src: svgPaths.arrow,
                        }}
                        prizeNumber={prizeNumber}
                        radiusLineWidth={0}
                        textColors={[colors.blue[500], colors.grey.white]}
                      />

                      <div className="new-ring-light-container">
                        {Array.from({ length: 24 }).map((_, index) => (
                          <span
                            key={index}
                            className={`light ${index % 2 === 0 ? 'even' : 'odd'}`}
                          />
                        ))}
                      </div>
                    </Box>
                  </Box>

                  <Button
                    size="large"
                    version="v2"
                    disabled={isDisable}
                    onClick={handleOnClickSpin}
                  >
                    Girar
                  </Button>
                </Flex>
              )}

              {!isThematicWheel && (
                <Box
                  className="shiny-coins"
                  sx={{ position: 'relative' }}
                  width="126%"
                  maxWidth="530px"
                >
                  <img
                    src={svgPaths.coins}
                    alt="Coins"
                    width="100%"
                    style={{ position: 'relative', zIndex: 1 }}
                  />

                  <img
                    src={svgPaths.star}
                    alt="star-1"
                    className="star-1"
                    style={{ position: 'absolute', top: '17%', left: '9%' }}
                  />

                  <img
                    src={svgPaths.star}
                    alt="star-2"
                    className="star-2"
                    style={{ position: 'absolute', top: '-17%', left: '47%' }}
                  />

                  <img
                    src={svgPaths.star}
                    alt="star-3"
                    className="star-3"
                    style={{ position: 'absolute', top: '8%', left: '79%' }}
                  />
                </Box>
              )}
            </>
          )}
        </Container>
      </Background>
    )
  }

  const formatPrizes = (rafflePrize: PrizeStocksRafflePrizeInterface[]): AwardInterface[] => {
    const prizes = rafflePrize.map((item) => ({
      isAwarded: item.isAwarded,
      option: item.description,
    }))

    if (prizes.length % 2 !== 0) prizes.push({ isAwarded: false, option: 'Não foi dessa vez' })

    return prizes.sort(() => Math.random() - 0.5)
  }

  useEffect(() => {
    const fetchPrizes = async () => {
      if (!campaignSlug) return

      try {
        setDataState((prev) => ({ ...prev, isLoading: true }))

        const data = contractNumber
          ? await PrizeHubService.getPrizes({ campaignSlug, contractNumber })
          : await PrizeHubService.getPreview({ campaignSlug })

        setPrizeStocksState(data)

        setDataState({ isLoading: false, data: formatPrizes(data.rafflePrize) })
      } catch {
        setDataState({ isLoading: false, data: [] })
      }
    }

    fetchPrizes()
  }, [campaignSlug, contractNumber])

  return <>{dataState.isLoading ? <></> : renderContent()}</>
}

export default Roulette
